import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Brain,
  Sparkles,
  Loader2,
  Send,
  MapPin,
  Building2,
  Calendar,
  Rss,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Archive,
  Quote,
  ExternalLink,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { AI_QUEUE_ITEMS } from "@/data/adminMock";
import {
  analyzeNews,
  archiveNews,
  reanalyzeNews,
  sendToReview,
  PIPELINE_STATUS,
  PIPELINE_STAGES,
} from "@/services/aiService";
import { ADMIN } from "@/constants/testIds";
import {
  SignalBadge,
  OMIBadge,
  EvidenceComposition,
  ConflictsSection,
  FieldEvidence,
  DecisionLog,
  SourcesList,
} from "./AIQueueSprint6";

/* ─── Presentational atoms ─────────────────────────────────────────── */

const scoreTone = (v) =>
  v >= 85 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : v >= 70 ? "text-intel-700 bg-intel-50 border-intel-200"
  : v >= 50 ? "text-amber-700 bg-amber-50 border-amber-200"
  :           "text-red-700 bg-red-50 border-red-200";

const ScorePill = ({ label, value }) => (
  <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${scoreTone(value)}`}>
    <span className="text-[11px] font-medium">{label}</span>
    <span className="font-mono text-sm font-semibold">{value}</span>
  </div>
);

const MetaChip = ({ Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white text-ink-500">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
    </span>
    <div>
      <div className="text-[11px] text-ink-500">{label}</div>
      <div className="text-sm font-medium text-ink-900">{value || "—"}</div>
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="text-[11px] font-medium uppercase tracking-widest text-intel-800">
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-ink-700">
    {items.map((s, i) => (
      <li key={i} className="flex items-start gap-2">
        <span aria-hidden className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-intel-500" />
        <span>{s}</span>
      </li>
    ))}
  </ul>
);

/* ─── Stage indicator ─────────────────────────────────────────────── */

const StageIndicator = ({ newsId, currentIndex, error }) => (
  <div
    data-testid={ADMIN.aiStageIndicator(newsId)}
    className="mt-4 grid grid-cols-1 gap-2 rounded-lg border border-surface-200 bg-white p-3 sm:grid-cols-3"
  >
    {PIPELINE_STAGES.map((s, i) => {
      const done   = i < currentIndex;
      const active = i === currentIndex && !error;
      const failed = i === currentIndex && !!error;
      return (
        <div
          key={s.key}
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
            failed
              ? "border border-red-200 bg-red-50 text-red-700"
              : done
                ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                : active
                  ? "border border-intel-200 bg-intel-50 text-intel-800"
                  : "border border-surface-200 bg-surface-50 text-ink-500"
          }`}
        >
          <span aria-hidden className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white">
            {done ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.75} />
            ) : failed ? (
              <XCircle className="h-3.5 w-3.5 text-red-600" strokeWidth={1.75} />
            ) : active ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-intel-800" strokeWidth={1.75} />
            ) : (
              <span className="text-[10px] font-mono text-ink-500">{i + 1}</span>
            )}
          </span>
          <div className="min-w-0">
            <div className="font-semibold">{s.label}</div>
            <div className="text-[10px] leading-tight opacity-80">{s.hint}</div>
          </div>
        </div>
      );
    })}
  </div>
);

/* ─── Result cards ─────────────────────────────────────────────────── */

const evidenceQuotes = (result) =>
  (result?.intelligence_v2?.evidence) || (result?.intelligence?.evidence) || [];
const intelInsights = (result) =>
  (result?.intelligence_v2?.key_insights) || (result?.intelligence?.key_insights) || [];
const intelActions = (result) =>
  (result?.intelligence_v2?.recommended_actions) || (result?.intelligence?.recommended_actions) || [];
const intelRisks = (result) =>
  (result?.intelligence_v2?.risks) || (result?.intelligence?.risks) || [];

const NotOpportunityCard = ({ newsId, result }) => {
  const intel = result?.intelligence_v2 || result?.intelligence || {};
  return (
    <div
      data-testid={ADMIN.aiRejectionCard(newsId)}
      className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
          <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
          {result?.correlation?.relationship === "duplicate"
            ? "مقالة مكرَّرة — لن يتم توليد فرصة جديدة"
            : "لا تُشكِّل هذه المقالة فرصة عملية"}
        </div>
        <div className="flex items-center gap-1.5">
          <SignalBadge newsId={newsId} correlation={result?.correlation} />
        </div>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-amber-900">
        {result?.rejection_reason}
      </p>
      {intel.evidence?.length > 0 && (
        <div className="mt-3">
          <SectionTitle>أدلّة من المقال</SectionTitle>
          <BulletList items={intel.evidence} />
        </div>
      )}
      <DecisionLog newsId={newsId} entries={result?.decision_log} />
    </div>
  );
};

const QualityFailedCard = ({ newsId, result }) => (
  <div
    data-testid={ADMIN.aiFailureCard(newsId)}
    className="mt-4 rounded-lg border border-red-200 bg-red-50/50 p-5"
  >
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
        <XCircle className="h-4 w-4" strokeWidth={1.75} />
        فشل فحص الجودة — يحتاج مراجعة يدوية
      </div>
      <div className="flex items-center gap-1.5">
        <SignalBadge newsId={newsId} correlation={result?.correlation} />
        <OMIBadge newsId={newsId} omi={result?.omi} />
      </div>
    </div>
    <p className="mt-2 text-sm leading-relaxed text-red-900">
      {result?.rejection_reason}
    </p>
    {result?.quality?.issues?.length > 0 && (
      <div className="mt-3">
        <SectionTitle>المشكلات المرصودة</SectionTitle>
        <BulletList items={result.quality.issues} />
      </div>
    )}
    {result?.quality?.review_notes && (
      <p className="mt-3 rounded-md border border-red-200 bg-white p-3 text-xs text-red-900">
        {result.quality.review_notes}
      </p>
    )}
    <EvidenceComposition newsId={newsId} composition={result?.evidence_composition} />
    <ConflictsSection newsId={newsId} conflicts={result?.conflicts} />
    <FieldEvidence newsId={newsId} fieldEvidence={result?.intelligence_v2?.field_evidence} />
    <DecisionLog newsId={newsId} entries={result?.decision_log} />
  </div>
);

const PipelineFailureCard = ({ newsId, message }) => (
  <div
    data-testid={ADMIN.aiFailureCard(newsId)}
    className="mt-4 rounded-lg border border-red-200 bg-red-50/40 p-5 text-sm text-red-800"
  >
    <div className="flex items-center gap-2 font-semibold">
      <XCircle className="h-4 w-4" strokeWidth={1.75} />
      تعذّر إتمام التحليل
    </div>
    <p className="mt-2 leading-relaxed text-red-900">{message}</p>
  </div>
);

const ApprovedOpportunityCard = ({ result, newsId, sourceUrl, onSend, onPublish, onArchive }) => {
  const intel = result.intelligence_v2 || result.intelligence || {};
  const quality = result.quality;
  const pub = result.publishing;
  return (
    <div
      data-testid={ADMIN.aiResultCard(newsId)}
      className="mt-4 rounded-lg border border-intel-200 bg-intel-50/40 p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-medium text-intel-800">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
          فرصة جاهزة للمراجعة التحريرية
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SignalBadge newsId={newsId} correlation={result.correlation} />
          <OMIBadge newsId={newsId} omi={result.omi} />
          <ScorePill label="نقاط FIE"  value={pub?.opportunity_score ?? intel?.opportunity_score ?? 0} />
          <ScorePill label="الثقة"     value={pub?.confidence_score ?? intel?.confidence_score ?? 0} />
          <ScorePill label="الجودة"    value={quality?.quality_score ?? 0} />
        </div>
      </div>

      <h4 className="mt-4 text-base font-semibold leading-snug text-ink-900 sm:text-lg">
        {pub?.title ?? intel?.title}
      </h4>

      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetaChip Icon={Sparkles}  label="النوع"   value={pub?.opportunity_type ?? intel?.opportunity_type} />
        <MetaChip Icon={Building2} label="القطاع"  value={pub?.industry ?? intel?.industry} />
        <MetaChip Icon={MapPin}    label="الدولة"  value={pub?.country ?? intel?.country} />
      </dl>

      <div className="mt-4 rounded-md border border-surface-200 bg-white p-4">
        <SectionTitle>الملخّص التنفيذي</SectionTitle>
        <p className="mt-2 text-sm leading-relaxed text-ink-700">
          {pub?.executive_summary ?? intel?.executive_summary}
        </p>
      </div>

      <EvidenceComposition newsId={newsId} composition={result.evidence_composition} />
      <ConflictsSection newsId={newsId} conflicts={result.conflicts} />
      <FieldEvidence newsId={newsId} fieldEvidence={intel?.field_evidence} />

      {intelInsights(result).length > 0 && (
        <div className="mt-4 rounded-md border border-surface-200 bg-white p-4">
          <SectionTitle>أبرز المؤشّرات</SectionTitle>
          <BulletList items={intelInsights(result)} />
        </div>
      )}

      {intelActions(result).length > 0 && (
        <div className="mt-4 rounded-md border border-surface-200 bg-white p-4">
          <SectionTitle>الإجراءات الموصى بها</SectionTitle>
          <BulletList items={intelActions(result)} />
        </div>
      )}

      {intelRisks(result).length > 0 && (
        <div className="mt-4 rounded-md border border-surface-200 bg-white p-4">
          <SectionTitle>المخاطر</SectionTitle>
          <BulletList items={intelRisks(result)} />
        </div>
      )}

      <SourcesList sources={result.correlation?.corroborating_sources} />

      {evidenceQuotes(result).length > 0 && (
        <div className="mt-4 rounded-md border border-surface-200 bg-white p-4">
          <SectionTitle>الأدلّة من المصدر</SectionTitle>
          <ul className="mt-2 space-y-2">
            {evidenceQuotes(result).map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-xs italic leading-relaxed text-ink-700">
                <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-500" strokeWidth={1.75} />
                <span>«{q}»</span>
              </li>
            ))}
          </ul>
          {(pub?.source_url || sourceUrl) && (
            <a
              href={pub?.source_url || sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-intel-800 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
              فتح المصدر الأصلي
            </a>
          )}
        </div>
      )}

      <DecisionLog newsId={newsId} entries={result.decision_log} />

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <span className="me-auto inline-flex items-center gap-1.5 text-[11px] text-ink-500">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.75} />
          حالة النشر: <span className="font-mono font-semibold">pending</span> · القرار للمسؤول
        </span>
        <button
          type="button"
          onClick={onArchive}
          data-testid={ADMIN.aiArchiveBtn(newsId)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-surface-200 bg-white px-3 text-xs font-medium text-ink-500 hover:border-red-200 hover:text-red-700"
        >
          <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />
          أرشفة
        </button>
        <button
          type="button"
          onClick={onSend}
          data-testid={ADMIN.aiSendToReviewBtn(newsId)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-intel-200 bg-white px-3 text-xs font-medium text-intel-800 hover:bg-intel-50"
        >
          <Send className="h-3.5 w-3.5" strokeWidth={1.75} />
          إرسال للمراجعة
        </button>
        <button
          type="button"
          onClick={onPublish}
          data-testid={ADMIN.aiPublishBtn(newsId)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-intel-800 px-4 text-xs font-medium text-white transition-colors hover:bg-intel-700"
        >
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.75} />
          نشر (المسؤول)
        </button>
      </div>
    </div>
  );
};

/* ─── Page ─────────────────────────────────────────────────────────── */

const stageIndexFor = (status) => {
  if (status === PIPELINE_STATUS.ANALYZING)      return 0;
  if (status === PIPELINE_STATUS.QUALITY_REVIEW) return 1;
  if (status === PIPELINE_STATUS.PREPARING)      return 2;
  return 3;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const postHumanDecision = async ({ signal_id, action, reason = "" }) => {
  const res = await fetch(`${BACKEND}/api/ai/human-decision`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ signal_id, action, reason }),
  });
  if (!res.ok) throw new Error("لم يُسجَّل القرار");
  return res.json();
};

export default function AIQueuePage() {
  const navigate = useNavigate();
  const [state, setState] = useState({});

  const setItem = (id, patch) =>
    setState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const runPipeline = async (item, { retry = false } = {}) => {
    setItem(item.id, {
      status: PIPELINE_STATUS.ANALYZING,
      currentStage: 0,
      result: undefined,
      error: undefined,
    });
    const onStage = (idx) => {
      setItem(item.id, {
        status: idx === 1 ? PIPELINE_STATUS.QUALITY_REVIEW
              : idx === 2 ? PIPELINE_STATUS.PREPARING
              :             PIPELINE_STATUS.ANALYZING,
        currentStage: idx,
      });
    };
    try {
      const call = retry ? reanalyzeNews : analyzeNews;
      const result = await call(item, onStage);
      setItem(item.id, {
        status: result.status,
        currentStage: stageIndexFor(result.status),
        result,
        error: undefined,
      });
    } catch (err) {
      setItem(item.id, {
        status: PIPELINE_STATUS.FAILED,
        currentStage: stageIndexFor(PIPELINE_STATUS.FAILED),
        error: err.message,
      });
      toast.error("تعذّر إتمام التحليل", { description: err.message });
    }
  };

  const handleAnalyze   = (item) => runPipeline(item);
  const handleReanalyze = (item) => runPipeline(item, { retry: true });

  const handleArchive = async (item) => {
    const signal_id = state[item.id]?.result?.correlation?.signal_id ?? item.id;
    try {
      await archiveNews(item.id);
      await postHumanDecision({ signal_id, action: "archive", reason: "أُرشفت من طابور الذكاء" });
      setItem(item.id, { status: PIPELINE_STATUS.ARCHIVED });
      toast.success("أُرشفت المقالة");
    } catch (err) {
      toast.error("تعذّرت الأرشفة", { description: err.message });
    }
  };

  const handleSend = async (item) => {
    const signal_id = state[item.id]?.result?.correlation?.signal_id ?? item.id;
    try {
      await sendToReview(item.id);
      await postHumanDecision({ signal_id, action: "edit", reason: "أُرسلت للمراجعة التحريرية" });
      setItem(item.id, { status: PIPELINE_STATUS.PENDING_REVIEW, sent: true });
      toast.success("أُرسلت الفرصة إلى قائمة المراجعة");
      setTimeout(() => navigate("/admin/review"), 900);
    } catch (err) {
      toast.error("تعذّر الإرسال", { description: err.message });
    }
  };

  const handlePublish = async (item) => {
    const signal_id = state[item.id]?.result?.correlation?.signal_id ?? item.id;
    try {
      await postHumanDecision({ signal_id, action: "publish", reason: "قرار نشر يدوي من المسؤول" });
      setItem(item.id, { status: PIPELINE_STATUS.PUBLISHED, published: true });
      toast.success("سُجِّل قرار النشر في سجلّ القرارات", {
        description: "الفرصة تنتقل الآن إلى قائمة الفرص المنشورة (في التنفيذ الفعلي).",
      });
    } catch (err) {
      toast.error("تعذّر تسجيل النشر", { description: err.message });
    }
  };

  const isRunning = (s) => [
    PIPELINE_STATUS.ANALYZING,
    PIPELINE_STATUS.QUALITY_REVIEW,
    PIPELINE_STATUS.PREPARING,
  ].includes(s);

  const pendingCount = AI_QUEUE_ITEMS.filter((n) => {
    const s = state[n.id];
    return !s || (s.status !== PIPELINE_STATUS.PENDING_REVIEW
                  && s.status !== PIPELINE_STATUS.ARCHIVED
                  && s.status !== PIPELINE_STATUS.PUBLISHED
                  && !s.sent && !s.published);
  }).length;

  return (
    <AdminLayout>
      <section className="space-y-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2">
              <p className="text-xs font-medium uppercase tracking-widest text-intel-800">
                الذكاء الآلي · واعٍ بالأدلّة
              </p>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                عيّنات تجريبية
              </span>
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
              طابور تحليل الأخبار
            </h2>
            <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
              يمرّ كل خبر بست مراحل: الارتباط والدمج ← وكيل الاستخبارات ← كشف التعارضات ← تصنيف الأدلّة ← OMI ← الجودة ← التجهيز. لا تُنشر أي فرصة تلقائياً — القرار للمسؤول.
            </p>
            <p className="max-w-2xl text-[11.5px] leading-relaxed text-ink-500">
              العناصر أدناه <span className="font-medium text-ink-700">مقالات عيّنة</span> مُعدّة لتشغيل خطّ تحليل Claude الحقيقي — لتدفّق بيانات RSS الحيّ استخدم <Link to="/admin/feed" className="text-fursa-700 hover:underline">طابور المراقبة الحيّ</Link>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg border border-surface-200 bg-white px-3 py-2 text-xs font-medium text-ink-700">
              <Brain className="h-4 w-4 text-intel-800" strokeWidth={1.75} />
              <span className="text-ink-500">في الطابور:</span>
              <span className="font-mono text-sm font-semibold text-ink-900">{pendingCount}</span>
            </div>
          </div>
        </header>

        <ul data-testid={ADMIN.aiQueueList} className="space-y-3">
          {AI_QUEUE_ITEMS.map((n) => {
            const s = state[n.id] ?? { status: PIPELINE_STATUS.QUEUED };
            const running = isRunning(s.status);
            const finished = [
              PIPELINE_STATUS.PENDING_REVIEW,
              PIPELINE_STATUS.NOT_OPPORTUNITY,
              PIPELINE_STATUS.QUALITY_FAILED,
              PIPELINE_STATUS.FAILED,
              PIPELINE_STATUS.ARCHIVED,
              PIPELINE_STATUS.PUBLISHED,
            ].includes(s.status);
            const isApproved = s.status === PIPELINE_STATUS.PENDING_REVIEW;

            return (
              <li
                key={n.id}
                data-testid={ADMIN.aiQueueItem(n.id)}
                className={`rounded-xl border bg-white p-5 transition-colors ${
                  s.status === PIPELINE_STATUS.ARCHIVED
                    ? "border-surface-200 bg-surface-50/60 opacity-70"
                    : s.status === PIPELINE_STATUS.PUBLISHED
                      ? "border-emerald-300 bg-emerald-50/40"
                      : isApproved
                        ? "border-intel-200"
                        : s.status === PIPELINE_STATUS.NOT_OPPORTUNITY
                          ? "border-amber-200"
                          : s.status === PIPELINE_STATUS.QUALITY_FAILED || s.status === PIPELINE_STATUS.FAILED
                            ? "border-red-200"
                            : "border-surface-200"
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold leading-snug text-ink-900 sm:text-lg">
                      {n.headline}
                    </h3>
                    <dl className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-500">
                      <div className="inline-flex items-center gap-1.5">
                        <Rss className="h-3.5 w-3.5" strokeWidth={1.75} />
                        <span>{n.source}</span>
                      </div>
                      <div className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
                        <span>{n.country}</span>
                      </div>
                      <div className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} />
                        <span className="font-mono">{n.publishedAt}</span>
                      </div>
                    </dl>
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {s.published ? (
                      <span className="inline-flex h-10 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-4 text-xs font-medium text-emerald-800">
                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
                        نُشرت بواسطة المسؤول
                      </span>
                    ) : s.sent ? (
                      <Link
                        to="/admin/review"
                        className="inline-flex h-10 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-4 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                      >
                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />
                        في قائمة المراجعة
                      </Link>
                    ) : s.status === PIPELINE_STATUS.ARCHIVED ? (
                      <span className="inline-flex h-10 items-center gap-1.5 rounded-md border border-surface-200 bg-white px-4 text-xs font-medium text-ink-500">
                        <Archive className="h-4 w-4" strokeWidth={1.75} />
                        مؤرشفة
                      </span>
                    ) : running ? (
                      <span className="inline-flex h-10 items-center gap-1.5 rounded-md bg-intel-800 px-4 text-xs font-medium text-white opacity-90">
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                        جارٍ التحليل…
                      </span>
                    ) : finished ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleReanalyze(n)}
                          data-testid={ADMIN.aiReanalyzeBtn(n.id)}
                          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-surface-200 bg-white px-3 text-xs font-medium text-ink-900 hover:border-intel-500 hover:text-intel-800"
                        >
                          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                          إعادة التحليل
                        </button>
                        <button
                          type="button"
                          onClick={() => handleArchive(n)}
                          data-testid={ADMIN.aiArchiveBtn(n.id)}
                          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-surface-200 bg-white px-3 text-xs font-medium text-ink-500 hover:border-red-200 hover:text-red-700"
                        >
                          <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />
                          أرشفة
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAnalyze(n)}
                        data-testid={ADMIN.aiAnalyzeBtn(n.id)}
                        className="inline-flex h-10 items-center gap-1.5 rounded-md bg-intel-800 px-4 text-xs font-medium text-white transition-colors hover:bg-intel-700"
                      >
                        <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                        تحليل
                      </button>
                    )}
                  </div>
                </div>

                {running && (
                  <StageIndicator newsId={n.id} currentIndex={s.currentStage ?? 0} />
                )}

                {isApproved && s.result && (
                  <ApprovedOpportunityCard
                    result={s.result}
                    newsId={n.id}
                    sourceUrl={n.sourceUrl}
                    onSend={() => handleSend(n)}
                    onPublish={() => handlePublish(n)}
                    onArchive={() => handleArchive(n)}
                  />
                )}

                {s.status === PIPELINE_STATUS.NOT_OPPORTUNITY && s.result && (
                  <NotOpportunityCard newsId={n.id} result={s.result} />
                )}

                {s.status === PIPELINE_STATUS.QUALITY_FAILED && s.result && (
                  <QualityFailedCard newsId={n.id} result={s.result} />
                )}

                {s.status === PIPELINE_STATUS.FAILED && (
                  <PipelineFailureCard newsId={n.id} message={s.error ?? "خطأ غير معروف"} />
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </AdminLayout>
  );
}
